import {useMemo, useState} from 'react'
import {Link} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Dialog, DialogContent, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {Input} from '@/components/ui/input'
import {Skeleton} from '@/components/ui/skeleton'
import {PaginationBar} from '@/components/ui/pagination-bar'
import {HelpIcon} from '@/components/help-icon'
import {DataTable, type Column} from '@/components/data-table'
import {FormDialogFooter} from '@/components/form-dialog-footer'
import {CompletarRosterDialog} from '@/components/bateria/completar-roster-dialog'
import {useAllEmployees, type Employee} from '@/hooks/empresa/identidad/use-employees'
import {useCreatePaquetes, type BateriaForma} from '@/hooks/bateria/use-bateria'
import {suggestForma} from '@/lib/bateria'
import {claveDeError, marcarErrorComoComunicado, rosterIncompletoDetalle} from '@/lib/errors'
import {normalize} from '@/lib/utils'
import {tope} from '@/lib/campos-texto'

const PAGE_SIZE = 10

/** Se valida al enviar y no al salir del campo: la invariante es la cardinalidad, no un texto. */
function validateSelection(count: number): string | null {
  return count > 0 ? null : 'admin.bateria.agregarDialog.selectRequired'
}

export function AgregarPaquetesDialog({
  businessId,
  evaluacionId,
  open,
  onOpenChange,
  existingEmployeeIds,
}: {
  businessId: string
  evaluacionId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  existingEmployeeIds: Set<string>
}) {
  const {t} = useTranslation()
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const {data: roster} = useAllEmployees(open ? businessId : undefined)
  const create = useCreatePaquetes(businessId, evaluacionId)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [globalForma, setGlobalForma] = useState<BateriaForma>('A')
  const [formaOverrides, setFormaOverrides] = useState<Record<string, BateriaForma>>({})
  const [error, setError] = useState<string | null>(null)
  // El 409 `roster_incompleto_bateria` nombra al empleado: se completa sin perder la selección y se reintenta el mismo envío.
  const [incompleto, setIncompleto] = useState<{employeeId: string; campos: string[]} | null>(null)

  // Filtrado y paginado en cliente: el servidor no puede filtrar «ya tiene paquete en esta evaluación».
  const addable = useMemo(
    () => (roster ?? []).filter((e) => e.status === 'active' && !existingEmployeeIds.has(e.id)),
    [roster, existingEmployeeIds]
  )
  const matching = useMemo(() => {
    const needle = normalize(search)
    if (!needle) return addable
    return addable.filter((e) => normalize(`${e.name} ${e.last_name} ${e.cedula}`).includes(needle))
  }, [addable, search])

  const total = matching.length
  const rows = useMemo(() => matching.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE), [matching, page])

  // Tres causas dejan la tabla vacía y solo una es «ya están todos»: `roster` las distingue.
  const vacio = useMemo(() => {
    if (addable.length > 0) return 'noResults'
    const gente = roster ?? []
    if (gente.length === 0) return 'sinNomina'
    if (gente.every((e) => e.status !== 'active')) return 'sinActivos'
    return 'noneAddable'
  }, [addable, roster])

  function formaFor(employee: Employee): BateriaForma {
    return formaOverrides[employee.id] ?? suggestForma(employee, globalForma)
  }

  function setForma(employeeId: string, forma: BateriaForma) {
    setFormaOverrides((prev) => ({...prev, [employeeId]: forma}))
  }

  function selectAllMatching() {
    setSelected(new Set(matching.map((e) => e.id)))
  }

  function clearSelection() {
    setSelected(new Set())
  }

  function close() {
    setSelected(new Set())
    setFormaOverrides({})
    setGlobalForma('A')
    setSearch('')
    setPage(1)
    setError(null)
    setIncompleto(null)
    create.reset()
    onOpenChange(false)
  }

  function submitSelection() {
    const picked = addable.filter((emp) => selected.has(emp.id))
    create.mutate(
      picked.map((emp) => ({employee_id: emp.id, forma: formaFor(emp)})),
      {
        onSuccess: close,
        onError: (err) => {
          const detalle = rosterIncompletoDetalle(err)
          if (detalle) {
            marcarErrorComoComunicado(err)
            setIncompleto(detalle)
            return
          }
          // Sin `translateApiError`: marcaría cualquier error, inesperado incluido, y la red global dejaría de reportarlo.
          setError(t(claveDeError(err)))
        },
      }
    )
  }

  function submit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    const picked = addable.filter((emp) => selected.has(emp.id))
    const selectionError = validateSelection(picked.length)
    if (selectionError) {
      setError(t(selectionError))
      return
    }
    submitSelection()
  }

  const columns: Column<Employee>[] = [
    {
      key: 'employee',
      header: t('admin.bateria.agregarDialog.employee'),
      cell: (e) => (
        <span className="flex items-baseline gap-2">
          <span className="min-w-0 truncate font-medium text-foreground">
            {e.name} {e.last_name}
          </span>
          <span className="shrink-0 font-mono text-xs text-muted-foreground">{e.cedula}</span>
        </span>
      ),
    },
    {
      key: 'forma',
      header: t('admin.bateria.agregarDialog.forma'),
      width: 'w-28',
      className: 'text-right',
      cell: (e) => (
        <div onClick={(ev) => ev.stopPropagation()} className="flex justify-end">
          <select
            data-testid={`forma-${e.id}`}
            value={formaFor(e)}
            onChange={(ev) => setForma(e.id, ev.target.value as BateriaForma)}
            className="h-7 rounded-md border border-input bg-transparent px-1.5 text-sm text-foreground">
            <option value="A">{t('admin.bateria.formaValue', {forma: 'A'})}</option>
            <option value="B">{t('admin.bateria.formaValue', {forma: 'B'})}</option>
          </select>
        </div>
      ),
    },
  ]

  const allMatchingSelected = matching.length > 0 && matching.every((e) => selected.has(e.id))

  return (
    <Dialog open={open} onOpenChange={(next) => !next && close()}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto" data-testid="agregar-paquetes-dialog">
        <DialogHeader>
          <DialogTitle>{t('admin.bateria.agregarDialog.title')}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" noValidate>
          <div className="flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 text-sm text-foreground">
              {t('admin.bateria.agregarDialog.globalForma')}
              <select
                data-testid="global-forma"
                value={globalForma}
                onChange={(e) => setGlobalForma(e.target.value as BateriaForma)}
                className="h-8 rounded-md border border-input bg-transparent px-1.5 text-sm">
                <option value="A">{t('admin.bateria.formaValue', {forma: 'A'})}</option>
                <option value="B">{t('admin.bateria.formaValue', {forma: 'B'})}</option>
              </select>
              <HelpIcon
                label={t('common.help.iconLabelFor', {campo: t('admin.bateria.detail.forma')})}
                testId="forma-help">
                {t('admin.bateria.agregarDialog.formaHelp')}
              </HelpIcon>
            </label>
            <Input
              maxLength={tope('BUSCA')}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value)
                setPage(1)
              }}
              placeholder={t('admin.bateria.agregarDialog.searchPlaceholder')}
              className="max-w-xs"
            />
          </div>

          {/* «Seleccionar todos» abarca todo el roster filtrado, no solo la página visible. */}
          <div className="flex flex-wrap items-center justify-between gap-2">
            <Button
              type="button"
              size="xs"
              variant="outline"
              data-testid="seleccionar-todos"
              disabled={matching.length === 0}
              onClick={allMatchingSelected ? clearSelection : selectAllMatching}>
              {allMatchingSelected
                ? t('admin.bateria.agregarDialog.deselectAll')
                : t('admin.bateria.agregarDialog.selectAll', {count: matching.length})}
            </Button>
            {selected.size > 0 && (
              <span className="font-mono text-[11px] text-muted-foreground" data-testid="seleccionados-count">
                {t('admin.bateria.agregarDialog.selectedCount', {count: selected.size})}
              </span>
            )}
          </div>

          {!roster ? (
            <Skeleton className="h-40 w-full" />
          ) : (
            <>
              <DataTable
                columns={columns}
                rows={rows}
                rowKey={(e) => e.id}
                selection={{selected, onChange: setSelected}}
                emptyState={
                  <div
                    className="space-y-3 rounded-lg border border-dashed border-border px-4 py-8 text-center text-sm text-muted-foreground"
                    data-testid="agregar-paquetes-vacio">
                    <p>{t(`admin.bateria.agregarDialog.${vacio}`)}</p>
                    {vacio !== 'noResults' && vacio !== 'noneAddable' && (
                      <Button asChild size="xs" variant="outline" data-testid="agregar-paquetes-ir-empleados">
                        <Link to={`/businesses/${businessId}/employees`}>
                          {t('admin.bateria.agregarDialog.irAEmpleados')}
                        </Link>
                      </Button>
                    )}
                  </div>
                }
              />
              <PaginationBar page={page} pageSize={PAGE_SIZE} total={total} onPageChange={setPage} />
            </>
          )}

          <FormDialogFooter
            error={error}
            pending={create.isPending}
            submitLabel={t('admin.bateria.agregarDialog.submit', {count: selected.size})}
            onCancel={close}
          />
        </form>
      </DialogContent>
      {incompleto && (
        <CompletarRosterDialog
          businessId={businessId}
          employeeId={incompleto.employeeId}
          campos={incompleto.campos}
          open
          onOpenChange={(next) => !next && setIncompleto(null)}
          onCompleted={() => {
            setIncompleto(null)
            submitSelection()
          }}
        />
      )}
    </Dialog>
  )
}
